import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { IconButton } from './icon-button';

export const DialogRoot = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

const OVERLAY =
  'fixed inset-0 z-40 bg-[color-mix(in_srgb,var(--text)_38%,transparent)] ' +
  'motion-safe:data-[state=open]:animate-[fade-in_240ms_var(--ease-gentle)]';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** 破壞性操作用 danger 讓標題與邊線帶警示色。 */
  tone?: 'default' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  tone = 'default',
  size = 'md',
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={OVERLAY} />
        <RadixDialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 w-[calc(100vw-4rem)] -translate-x-1/2 -translate-y-1/2',
            'bezel-shell rounded-[var(--radius-shell)] p-1.5 shadow-[var(--shadow-lifted)]',
            'motion-safe:data-[state=open]:animate-[dialog-in_260ms_var(--ease-gentle)]',
            SIZES[size],
          )}
        >
          <div
            className={cn(
              'bezel-core max-h-[80vh] overflow-y-auto rounded-[calc(var(--radius-shell)-0.375rem)] p-6',
              tone === 'danger' && 'ring-1 ring-danger/30',
            )}
          >
            <div className="mb-4 flex items-start gap-4">
              <div className="min-w-0 flex-1 space-y-1">
                <RadixDialog.Title
                  className={cn(
                    'text-[1.125rem] font-semibold tracking-tight',
                    tone === 'danger' && 'text-danger',
                  )}
                >
                  {title}
                </RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className="text-[0.875rem] leading-relaxed text-text-muted">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              <RadixDialog.Close asChild>
                <IconButton label="關閉" icon={<X size={18} />} className="-mt-1 -mr-2" />
              </RadixDialog.Close>
            </div>

            {children}

            {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/** 右側詳細抽屜：滑入並帶輕微 fade，不拉伸內容（spec 23.2）。 */
export function Drawer({ open, onOpenChange, title, subtitle, children, footer }: DrawerProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={OVERLAY} />
        <RadixDialog.Content
          className={cn(
            'fixed top-0 right-0 z-50 flex h-[100dvh] w-[min(34rem,calc(100vw-6rem))] flex-col',
            'bg-surface-raised shadow-[var(--shadow-lifted)]',
            'motion-safe:data-[state=open]:animate-[drawer-in_300ms_var(--ease-gentle)]',
          )}
        >
          <header className="flex items-start gap-4 border-b border-border px-6 py-5">
            <div className="min-w-0 flex-1 space-y-1">
              <RadixDialog.Title className="truncate text-[1.0625rem] font-semibold tracking-tight">
                {title}
              </RadixDialog.Title>
              {subtitle && (
                <RadixDialog.Description asChild>
                  <div className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-text-muted">
                    {subtitle}
                  </div>
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton label="關閉抽屜" icon={<X size={18} />} className="-mt-1 -mr-2" />
            </RadixDialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <footer className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
              {footer}
            </footer>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
